// 1. Angular Core e Framework
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  OnInit,
  ViewChild,
  inject,
  OnDestroy,
  signal,
  computed,
} from '@angular/core';
import { FormGroup, FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

// 2. RxJS (Observables e Operadores)
import {
  catchError,
  EMPTY,
  finalize,
  forkJoin,
  interval,
  Observable,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
  tap,
  throwError,
} from 'rxjs';

// 3. PO-UI (Módulos e Serviços)
import {
  PoButtonModule,
  PoDialogModule,
  PoDialogService,
  PoDividerModule,
  PoFieldModule,
  PoIconModule,
  PoInfoModule,
  PoLoadingModule,
  PoModalModule,
  PoPageModule,
  PoSelectOption,
  PoGaugeRanges,
} from '@po-ui/ng-components';

// 4. PO-UI (Types e Componentes de Referência)
import {
  PoInfoOrientation,
  PoInputComponent,
  PoModalComponent,
  PoRadioGroupOption,
} from '@po-ui/ng-components';

// 5. Componentes e Serviços Internos
import { ToasterComponent } from '../../../shared/components/toaster/toaster.component';
import { CountService } from '../../../shared/service/count.service';
import { ProductionOrderService } from '../../../shared/service/production-order.service';
import { ToasterService } from '../../../shared/service/toaster.service';
import { TokenService } from '../../../shared/service/token.service';
import { NotificationService } from '../../../shared/service/notification.service';
import {PostFinalizationCountBody} from '../../../models/finalization-count.model';

// 6. Modelos e Tipos
import {
  CountItem,
  ProductDetail,
  SummaryByOp,
  SummaryByProduct,
} from '../../../models/finalization-count-items';
import { ProductionService } from '../../../shared/service/production.service';
import { ProductionReason } from '../../../models/production-reason.model';
import {
  ProductionOrderTimePayload,
  QuantityProgressResponse,
} from '../../../models/production-order.model';
import {
  FinalizationOrderPayload,
  FinalizationOrderResponse,
} from '../../../models/finalization-order.model';
import { CustomProgressComponent } from '../../../shared/components/custom-progress/custom-progress.component';
import { RhythmGaugeComponent } from '../../../shared/components/app-rhythm-gauge/rhythm-gauge.component';

type ViewMode = 'op' | 'product';

@Component({
  standalone: true,
  selector: 'app-finalization-count-items',
  templateUrl: './finalization-count-items.component.html',
  styleUrls: ['./finalization-count-items.component.css'],
  imports: [
    CommonModule,
    FormsModule,
    PoPageModule,
    PoInfoModule,
    PoButtonModule,
    PoDividerModule,
    PoIconModule,
    PoFieldModule,
    PoLoadingModule,
    ToasterComponent,
    PoModalModule,
    PoDialogModule,
    CustomProgressComponent,
    RhythmGaugeComponent,
  ],
})
export class FinalizationCountItemsComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('barcodeInput', { static: true }) barcodeInput!: PoInputComponent;
  @ViewChild('confirmModal') confirmModal!: PoModalComponent;
  @ViewChild('summaryModal') summaryModal!: PoModalComponent;
  @ViewChild('opQuestionModal') opQuestionModal!: PoModalComponent;
  @ViewChild('pauseReasonModal') pauseReasonModal!: PoModalComponent;
  isLoading: boolean = false;
  size = 'sm';
  text = 'Carregando';
  countForm!: FormGroup;
  PoInfoOrientation = PoInfoOrientation;
  viewMode: ViewMode = 'op';
  readonly viewOptions: PoRadioGroupOption[] = [
    { label: 'Agrupar por OP', value: 'op' },
    { label: 'Agrupar por Produto', value: 'product' },
  ];
  countDetails: any;
  barCode: string = '';
  itemToRemoveSequence: number | null = null;
  resumoFinalizacaoPorOp: any[] = [];
  resumoFinalizacaoPorProduto: any[] = [];
  pauseReasonsOptions: PoSelectOption[] = [];
  progressTitle =
    window.innerWidth < 768
      ? 'Progresso da Finalização da OP'
      : 'Progresso da Finalização da Ordem de Produção';

  timeTitle =
    window.innerWidth < 768
      ? 'Tempo da Finalização da OP'
      : 'Tempo da Finalização da Ordem de Produção';
  timeStatus = signal<'idle' | 'tracking' | 'paused'>('idle');
  selectedPauseReason: number | null = null;
  readProductionOrderKeys = signal<any[]>([]);
  disableOpWarningGlobal = signal<boolean>(false);
  currentOpWarning = signal<string>('');
  isPauseModalVisible = signal<boolean>(false);
  theoreticalTime = signal<number>(0);
  actualProductiveTime = signal<number>(0);
  quantityProgress = signal<QuantityProgressResponse | null>(null);
  rhythmValue = signal<number>(0);
  rhythmDescription = signal<string>('0% - Sem dados');
  rhythmRanges = signal<Array<PoGaugeRanges>>([]);
  rhythmCurrentPercentage = signal<number>(0);
  rhythmPointerRotation = signal<number>(0);

  value = computed(() => {
    const percent = this.quantityProgress()?.progressPercentage ?? 0;
    return Math.floor(percent);
  });

  infoText = computed(() => {
    const data = this.quantityProgress();
    if (!data) return 'Carregando progresso...';
    return `${data.finishedQuantity + data.inProgressQuantity} / ${data.totalQuantity} unidades`;
  });

  timeProgressPercent = computed(() => {
    const theoretical = this.theoreticalTime();
    const actual = this.actualProductiveTime();

    if (!theoretical || theoretical === 0) return 0;

    return (actual / theoretical) * 100;
  });

  timeProgressWidth = computed(() => {
    const percent = this.timeProgressPercent();
    return percent > 100 ? 100 : percent;
  });

  timeInfoText = computed(() => {
    const actual = this.actualProductiveTime().toFixed(2);
    const theoretical = this.theoreticalTime().toFixed(2);
    return `${actual} / ${theoretical} min`;
  });

  hasNoOperationalSequence = computed(() => {
    const theoretical = this.theoreticalTime();
    return theoretical === 0 || theoretical === null || theoretical === undefined;
  });

  private lastMetricsUpdate = signal<number>(0);
  private readonly REFRESH_INTERVAL_MS = 60000; // 1 minuto em milissegundos

  private pendingByOpProduct = new Map<string, number>();
  private modalResolver?: (value: 'YES' | 'NO' | 'ALWAYS') => void;

  private makeKey(branch: number, cycle: number, order: number, product: number): string {
    return `${branch}-${cycle}-${order}-${product}`;
  }

  private heartbeatSubscription?: Subscription;
  private destroy$ = new Subject<void>();

  private router = inject(Router);
  private productionOrderService = inject(ProductionOrderService);
  private productionService = inject(ProductionService);
  private tokenService = inject(TokenService);
  private toasterService = inject(ToasterService);
  private countService = inject(CountService);
  private poDialog = inject(PoDialogService);
  private readonly notificationService = inject(NotificationService);

  private metricsPolling?: Subscription;

  ngOnInit(): void {
    this.isLoading = true;
    this.countDetails = history.state?.countDetails;
    const branchCode = Number(this.tokenService.getBranch());
    this.loadCount(branchCode, this.countDetails.code).subscribe();
    this.loadPauseReasons();

    this.metricsPolling = interval(10000).subscribe(() => {
      this.refreshMetricsIfNeeded();
    });
  }

  ngAfterViewInit(): void {
    this.focusInputBarcode();
  }

  ngOnDestroy(): void {
    this.stopHeartbeat();
    this.metricsPolling?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadPauseReasons(): void {
    if (!this.countDetails.isSingleReading) {
      return;
    }

    this.productionService.getProductionReasons().subscribe({
      next: (reasons: ProductionReason[]) => {
        this.pauseReasonsOptions = reasons
          .filter(reason => reason.description && reason.description.trim() !== '')
          .map(reason => ({
            label: reason.description,
            value: reason.reasonCode,
          }));
      },
      error: err => {
        this.toasterService.triggerAlert('Erro ao carregar motivos de parada.', 'error');
      },
    });
  }

  onPauseReasonChange(reasonCode: number): void {
    this.selectedPauseReason = reasonCode;
  }

  startTimeTracking() {
    const payload = this.buildTimePayload(1);
    this.productionOrderService
      .postTimeTrack(payload)
      .pipe(
        tap(() => {
          this.timeStatus.set('tracking');
          this.toasterService.triggerAlert('Registro de tempo iniciado.', 'success');
          this.startHeartbeat();
        }),
        catchError(err => {
          this.toasterService.triggerAlert('Erro ao iniciar registro de tempo.', 'error');
          return EMPTY;
        }),
      )
      .subscribe();
  }

  pauseTimeTracking() {
    if (this.pauseReasonsOptions.length === 0) {
      this.loadPauseReasons();
    }

    this.selectedPauseReason = null;
    this.isPauseModalVisible.set(true);
    this.pauseReasonModal.open();
  }

  confirmPause() {
    const payload = this.buildTimePayload(2, this.selectedPauseReason);

    this.isLoading = true;
    this.productionOrderService
      .postTimeTrack(payload)
      .pipe(
        tap(() => {
          this.timeStatus.set('paused');
          this.toasterService.triggerAlert('Registro de tempo pausado.', 'warning');
          this.pauseReasonModal.close();
          this.isPauseModalVisible.set(false);
        }),
        catchError(() => {
          this.toasterService.triggerAlert('Erro ao pausar rastreio.', 'error');
          return EMPTY;
        }),
        finalize(() => (this.isLoading = false)),
      )
      .subscribe();
  }

  closePauseModal() {
    this.pauseReasonModal.close();
    this.isPauseModalVisible.set(false);
  }

  loadCount(branchCode: number, countCode: number): Observable<any> {
    this.isLoading = true;
    return this.countService.getCountByCode(branchCode, countCode).pipe(
      tap(res => {
        this.countDetails.code = res.code;
        this.countDetails.items = [...res.items].sort((a, b) => b.sequence - a.sequence);
        this.countDetails.isSingleReading = res.isSingleProductionOrder;
        this.countDetails.productionOrderBranchCode = res.productionOrderBranchCode;
        this.countDetails.productionOrderCode = res.productionOrderCode;
        this.countDetails.productionOrderCycleCode = res.productionOrderCycleCode;

        this.populateReadOrderKeysFromItems(this.countDetails.items);

        this.recalculateCountDetails();
        this.isLoading = false;
      }),
      catchError(err => {
        console.error('Erro no loadCount:', err);
        this.isLoading = false;
        return throwError(() => err);
      }),
      finalize(() => {
        this.loadTheoreticalTime();
        this.refreshMetricsIfNeeded(true);
      }),
    );
  }

  removeItem(sequence: number): void {
    this.itemToRemoveSequence = sequence;
    this.poDialog.confirm({
      title: 'Confirmar exclusão',
      message: 'Deseja realmente excluir este item da contagem?',
      confirm: () => this.confirmRemoveItem(),
    });
  }

  confirmRemoveItem(): void {
    const branchCode = Number(this.tokenService.getBranch());
    const countCode = this.countDetails.code;
    const item = this.countDetails.items.find((i: any) => i.sequence === this.itemToRemoveSequence);
    if (!item) return;

    this.countService
      .deleteCountItem(branchCode, countCode, item.sequence)
      .pipe(
        switchMap(() => {
          this.toasterService.triggerAlert('Item removido com sucesso.', 'success');
          return this.loadCount(branchCode, countCode);
        }),
      )
      .subscribe({
        error: err => {
          this.toasterService.triggerAlert(
            err?.error?.[0]?.message || 'Erro ao processar operação.',
            'error',
          );
        },
        complete: () => {
          this.itemToRemoveSequence = null;
        },
      });
  }

  cancelRemoveItem(): void {
    this.itemToRemoveSequence = null;
    this.confirmModal.close();
  }

  goBack(): void {
    this.stopHeartbeat();
    this.metricsPolling?.unsubscribe();
    this.router.navigate(['/production-order-finalization']);
  }

  onScan(): void {
    const barCode = this.barCode.trim();

    if (!barCode || !this.isValidBarcode(barCode)) {
      this.focusInputBarcode();
      return;
    }

    this.isLoading = true;
    const branchCode = Number(this.tokenService.getBranch()!);

    this.productionOrderService
      .validateBarCode(
        branchCode,
        barCode,
        null,
        null,
        this.countDetails.code,
        this.countDetails.productionOrderBranchCode,
        this.countDetails.productionOrderCycleCode,
        this.countDetails.productionOrderCode,
      )
      .pipe(
        switchMap(async res => {
          if (!this.countDetails.isSingleReading) {
            const decision = await this.checkNewProductionOrder(res.productionOrderKey);
            if (decision === 'NO') {
              throw new Error('USER_ABORTED');
            }
          }
          return res;
        }),
        switchMap(res =>
          this.countService
            .postCountItem(
              branchCode,
              this.countDetails.code,
              res.productCode,
              barCode,
              1,
              this.countDetails.productionOrderBranchCode,
              this.countDetails.productionOrderCycleCode,
              this.countDetails.productionOrderCode,
            )
            .pipe(
              tap(() => {
                this.updatePendingMap(res);
                if (res.productionOrderKey) {
                  this.storeUniqueOrderKey(res.productionOrderKey);
                }
              }),
            ),
        ),
        finalize(() => {
          this.isLoading = false;
          this.resetBarcodeField();
        }),
      )
      .subscribe({
        next: insertedItem => {
          this.addItemLocal(insertedItem);
          this.refreshMetricsIfNeeded(true);
        },
        error: err => {
          if (err.message === 'USER_ABORTED') return;
          const message = err?.error?.[0]?.message || 'Falha na operação de leitura.';
          this.toasterService.triggerAlert(message, 'error');
        },
      });
  }

  closeQuestionModal(choice: 'YES' | 'NO' | 'ALWAYS'): void {
    this.opQuestionModal.close();
    this.modalResolver?.(choice);
  }

  openSummary(): void {
    const groupedData = this.groupItemsByOp();
    this.resumoFinalizacaoPorOp = this.transformToOpView(groupedData);
    this.resumoFinalizacaoPorProduto = this.transformToProductView(groupedData);
    this.summaryModal.open();
  }

  finalizeOp(): void {
    const branchCode = Number(this.tokenService.getBranch()!);
    const countCode = this.countDetails.code;

    this.loadCount(branchCode, countCode)
      .pipe(
        catchError(err => {
          const message = err.error?.[0]?.message || err.message || 'Erro inesperado ao finalizar OP.';
          this.toasterService.triggerAlert(message, 'error');
          return EMPTY;
        }),
      )
      .subscribe(() => {
        const payload = this.buildFinalizePayload(branchCode);
        this.productionOrderService.finalizeOrder(payload).subscribe({
          next: () => {
            this.notificationService.emitFinalizationResult({ countCode, success: true });
          },
          error: err => {
            const message =
              err.error?.[0]?.message || err.message || 'Erro inesperado ao finalizar OP.';
            this.notificationService.error(message);
            this.notificationService.emitFinalizationResult({ countCode, success: false, message });
          },
        });

        this.notificationService.markAsProcessing(countCode);
        this.notificationService.info(
          `A finalização da contagem ${countCode} está sendo realizada. Seu resultado estará pronto em alguns segundos.`,
          `finalization-${countCode}`,
        );

        this.goBack();
      });
  }

  finalizeAndCreateNew(): void {
    const branchCode = Number(this.tokenService.getBranch()!);
    const countCode = this.countDetails.code;

    const body = {
      branchCode,
      component: this.tokenService.getComponentCode('production-order-finalization')!,
      description: this.countDetails.description,
      productionOrderCycleCode: this.countDetails.productionOrderCycleCode,
      productionOrderCode: this.countDetails.productionOrderCode,
    };

    this.loadCount(branchCode, countCode)
      .pipe(
        catchError(err => {
          const message = err.error?.[0]?.message || err.message || 'Erro inesperado ao finalizar OP.';
          this.toasterService.triggerAlert(message, 'error');
          return EMPTY;
        }),
      )
      .subscribe(() => {
        const payload = this.buildFinalizePayload(branchCode);
        this.productionOrderService.finalizeOrder(payload).subscribe({
          next: () => {
            this.notificationService.emitFinalizationResult({ countCode, success: true });
          },
          error: err => {
            const message =
              err.error?.[0]?.message || err.message || 'Erro inesperado ao finalizar OP.';
            this.notificationService.error(message);
            this.notificationService.emitFinalizationResult({ countCode, success: false, message });
          },
        });

        this.notificationService.markAsProcessing(countCode);
        this.notificationService.info(
          `A finalização da contagem ${countCode} está sendo realizada. Seu resultado estará pronto em alguns segundos.`,
          `finalization-${countCode}`,
        );

        this.countService.postFinalizationCount(body).subscribe({
          next: ({ code }) => {

            const navigationExtras = {
              state: {
                countDetails: {
                  branchCode: branchCode,
                  code: code,
                  description: body.description,
                  isSingleReading: this.countDetails.isSingleReading,
                },
              },
            };

            this.router.navigate(['/finalization-count-items'], navigationExtras).then(() => {

              this.countDetails = navigationExtras.state.countDetails;
              this.isLoading = true;

              this.loadCount(branchCode, code).subscribe(() => {
                this.isLoading = false;
                this.toasterService.triggerAlert('Nova contagem aberta com sucesso!', 'success');
              });

            });

          },
          error: err => {
            const message = err?.error?.[0]?.message || 'Falha na criação da contagem.';
            this.toasterService.triggerAlert(message, 'error');
            console.error('Erro ao criar nova contagem:', err);
          },
        });
      });
  }

  private buildTimePayload(
    situation: number,
    reason: number | null = null,
  ): ProductionOrderTimePayload {
    const payload: ProductionOrderTimePayload = {
      branchCode: Number(
        this.countDetails.productionOrderBranchCode || this.tokenService.getBranch(),
      ),
      cycleCode: Number(this.countDetails.productionOrderCycleCode || 0),
      orderCode: Number(this.countDetails.productionOrderCode || 0),
      employeeCode: Number(this.tokenService.getUserCode() || 0),
      situationType: situation,
    };

    if (reason !== null) {
      payload.reasonCode = reason;
    }

    return payload;
  }

  private focusInputBarcode(): void {
    setTimeout(() => {
      if (this.barcodeInput) {
        this.barcodeInput.focus();
      }
    }, 150);
  }

  private recalculateCountDetails(): void {
    const items = this.countDetails.items || [];
    this.countDetails.quantity = items.reduce(
      (sum: any, item: { quantity: any }) => sum + (item.quantity || 0),
      0,
    );

    const uniquePacks = new Set(
      items
        .map((i: { pack: any }) => i.pack)
        .filter((p: null | undefined) => p !== null && p !== undefined),
    );
    this.countDetails.pack = uniquePacks.size;

    for (const it of items) {
      const k = this.makeKey(
        it.productionOrderBranchCode,
        it.productionOrderCycleCode,
        it.productionOrderCode,
        it.productCode,
      );

      if (!this.pendingByOpProduct.has(k) && typeof it.lastLocalProductQuantity === 'number') {
        this.pendingByOpProduct.set(k, it.lastLocalProductQuantity);
      }
    }
  }

  private addItemLocal(newItem: any): void {
    this.countDetails.items.push(newItem);
    this.countDetails.items.sort((a: any, b: any) => b.sequence - a.sequence);
    this.recalculateCountDetails();
  }

  private isValidBarcode(barCode: string): boolean {
    if (!barCode) {
      this.toasterService.triggerAlert('Código de barras vazio.', 'warning');
      return false;
    }
    let alreadyScanned = false;

    if (!this.countDetails.isSingleReading) {
      alreadyScanned = this.countDetails.items.some((item: any) => item.barCode === barCode);
    } else {
      const hasSuffix = /[-@#]\d{5}$/.test(barCode);

      if (hasSuffix) {
        alreadyScanned = this.countDetails.items.some((item: any) => item.barCode === barCode);
      }
    }

    if (alreadyScanned) {
      this.toasterService.triggerAlert('Este código de barras já foi lido na contagem.', 'warning');
      this.resetBarcodeField();
      return false;
    }

    return true;
  }

  private updatePendingMap(res: any): void {
    const pk = res?.productionOrderKey;
    const lastQty = res?.lastOrderLocation?.productQuantity;
    const prodCode = res?.productCode;

    if (pk && typeof lastQty === 'number' && typeof prodCode === 'number') {
      const pendKey = this.makeKey(pk.branchCode, pk.cycleCode, pk.orderCode, prodCode);
      this.pendingByOpProduct.set(pendKey, lastQty);
    }
  }

  private resetBarcodeField(): void {
    this.barCode = '';
    this.focusInputBarcode();
  }

  private populateReadOrderKeysFromItems(items: any[]): void {
    const uniqueKeys: any[] = [];
    items.forEach(item => {
      const key = {
        branchCode: Number(item.productionOrderBranchCode),
        cycleCode: Number(item.productionOrderCycleCode),
        orderCode: Number(item.productionOrderCode),
      };
      if (!uniqueKeys.some(existing => this.compareKeys(existing, key)) && key.orderCode) {
        uniqueKeys.push(key);
      }
    });
    this.readProductionOrderKeys.set(uniqueKeys);
  }

  private storeUniqueOrderKey(key: any): void {
    this.readProductionOrderKeys.update(keys => {
      const exists = keys.some(k => this.compareKeys(k, key));
      return exists ? keys : [...keys, key];
    });
  }

  private compareKeys(k1: any, k2: any): boolean {
    return (
      k1.branchCode === k2.branchCode &&
      k1.cycleCode === k2.cycleCode &&
      k1.orderCode === k2.orderCode
    );
  }

  private async checkNewProductionOrder(newKey: any): Promise<'YES' | 'NO' | 'ALWAYS'> {
    if (this.disableOpWarningGlobal() || !newKey) return 'ALWAYS';

    const currentKeys = this.readProductionOrderKeys();

    if (currentKeys.length === 0 || currentKeys.some(k => this.compareKeys(k, newKey))) {
      return 'YES';
    }

    this.currentOpWarning.set(`${newKey.branchCode}/${newKey.cycleCode}/${newKey.orderCode}`);
    this.opQuestionModal.open();

    const userChoice = await new Promise<'YES' | 'NO' | 'ALWAYS'>(resolve => {
      this.modalResolver = resolve;
    });

    if (userChoice === 'ALWAYS') {
      this.disableOpWarningGlobal.set(true);
    }
    return userChoice;
  }

  private buildFinalizePayload(branchCode: number): FinalizationOrderPayload {
    return {
      branchCode: branchCode,
      finalizationType: 1,
      operationCode: null,
      componentId: null,
      ...(!this.countDetails.isSingleReading && {
        movements: this.buildGroupedMovements(branchCode),
      }),
      counts: [
        {
          branchCode: branchCode,
          code: Number(this.countDetails.code),
        },
      ],
    };
  }

  private buildGroupedMovements(branchCode: number): any[] {
    return Object.values(
      this.countDetails.items.reduce((acc: any, item: any) => {
        const key = [
          item.productionOrderCycleCode || 0,
          item.productionOrderCode || 0,
          item.productCode || 0,
          item.placeProgrammingCode || 0,
        ].join('|');
        if (!acc[key]) {
          acc[key] = {
            branchCode,
            cycleCode: item.productionOrderCycleCode || 0,
            orderCode: item.productionOrderCode || 0,
            productCode: item.productCode || 0,
            productQuantity: 0,
            referenceId: null,
            placeCode: item.placeProgrammingCode,
          };
        }
        acc[key].productQuantity += Number(item.quantity) || 0;
        return acc;
      }, {}),
    ).map((movement: any) => ({
      ...movement,
      productQuantity: Number(movement.productQuantity.toFixed(3)),
    }));
  }

  private isValidResponse(data: FinalizationOrderResponse): boolean {
    const validStatus = [200, 202, 'Success'];
    return data && validStatus.includes(data.status);
  }

  private groupItemsByOp(): Record<string, Map<number, ProductDetail>> {
    const grouped: Record<string, Map<number, ProductDetail>> = {};

    (this.countDetails.items || []).forEach((item: CountItem) => {
      const opKey = `${item.productionOrderBranchCode}-${item.productionOrderCycleCode}-${item.productionOrderCode}`;

      if (!grouped[opKey]) {
        grouped[opKey] = new Map<number, ProductDetail>();
      }

      this.updateProductInMap(grouped[opKey], item);
    });

    return grouped;
  }

  private updateProductInMap(map: Map<number, ProductDetail>, item: any): void {
    const prodKey = this.makeKey(
      item.productionOrderBranchCode,
      item.productionOrderCycleCode,
      item.productionOrderCode,
      item.productCode,
    );

    if (!map.has(item.productCode)) {
      const pendente = this.pendingByOpProduct.get(prodKey) ?? item.lastLocalProductQuantity ?? 0;

      map.set(item.productCode, {
        productCode: item.productCode,
        description: item.descriptionProduct,
        contado: 0,
        pendente,
        diferenca: 0,
      });
    }

    const p = map.get(item.productCode)!;
    p.contado += item.quantity;
    p.diferenca = p.pendente - p.contado;
  }

  private transformToOpView(grouped: Record<string, Map<number, ProductDetail>>): SummaryByOp[] {
    const occurrenceMap = this.buildOccurrenceMap(grouped);

    return Object.entries(grouped).map(([orderKey, productsMap]) => {
      const produtos = Array.from(productsMap.values());
      const totalContado = produtos.reduce((acc, p) => acc + p.contado, 0);
      const totalPendente = produtos.reduce((acc, p) => acc + p.pendente, 0);
      const totalDiferenca = totalPendente - totalContado;
      return {
        orderKey,
        totalContado,
        totalPendente,
        totalDiferenca,
        produtos: produtos.map(prod => ({
          ...prod,
          operations: occurrenceMap.get(prod.productCode) || [],
        })),
      };
    });
  }

  private transformToProductView(
    grouped: Record<string, Map<number, ProductDetail>>,
  ): SummaryByProduct[] {
    const productsMap = new Map<number, SummaryByProduct>();

    Object.entries(grouped).forEach(([orderKey, products]) => {
      products.forEach(prod => {
        if (!productsMap.has(prod.productCode)) {
          productsMap.set(prod.productCode, this.createNewProductSummary(prod));
        }

        const summary = productsMap.get(prod.productCode)!;
        this.accumulateProductTotals(summary, prod, orderKey);
      });
    });

    return Array.from(productsMap.values());
  }

  private buildOccurrenceMap(
    grouped: Record<string, Map<number, ProductDetail>>,
  ): Map<number, string[]> {
    const map = new Map<number, string[]>();
    Object.entries(grouped).forEach(([opKey, products]) => {
      products.forEach(p => {
        const ops = map.get(p.productCode) || [];
        if (!ops.includes(opKey)) map.set(p.productCode, [...ops, opKey]);
      });
    });
    return map;
  }

  private createNewProductSummary(prod: ProductDetail): SummaryByProduct {
    return {
      productCode: prod.productCode,
      description: prod.description,
      totalContado: 0,
      totalPendente: 0,
      totalDiferenca: 0,
      ops: [],
    };
  }

  private accumulateProductTotals(
    summary: SummaryByProduct,
    prod: ProductDetail,
    orderKey: string,
  ): void {
    summary.totalContado += prod.contado;
    summary.totalPendente += prod.pendente;
    summary.totalDiferenca += prod.diferenca;
    summary.ops.push({
      orderKey,
      contado: prod.contado,
      pendente: prod.pendente,
      diferenca: prod.diferenca,
    });
  }

  private startHeartbeat() {
    if (this.heartbeatSubscription) return;

    this.sendHeartbeat();

    this.heartbeatSubscription = interval(30000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.sendHeartbeat();
      });
  }

  private sendHeartbeat() {
    const currentSituation = this.timeStatus() === 'paused' ? 2 : 1;
    const payload = this.buildTimePayload(currentSituation);

    this.productionOrderService
      .postTimeHeartbeat(payload)
      .pipe(
        catchError(err => {
          console.error('Falha no Heartbeat', err);
          return EMPTY;
        }),
      )
      .subscribe();
  }

  private stopHeartbeat() {
    if (this.heartbeatSubscription) {
      this.heartbeatSubscription.unsubscribe();
      this.heartbeatSubscription = undefined;
    }
  }

  public loadTheoreticalTime(): void {
    const { productionOrderCode, productionOrderCycleCode, branchCode } = this.countDetails;

    if (!this.countDetails.isSingleReading) {
      return;
    }

    this.productionOrderService
      .getTheoreticalTime(branchCode, productionOrderCycleCode, productionOrderCode)
      .subscribe(res => this.theoreticalTime.set(res.totalTheoreticalMinutes));
  }

  public refreshMetricsIfNeeded(force: boolean = false): void {
    if (!this.countDetails.isSingleReading) {
      return;
    }

    const now = Date.now();
    if (this.timeStatus() == 'tracking') {
      this.sendHeartbeat();
    }

    const timeSinceLastUpdate = now - this.lastMetricsUpdate();

    if (force || timeSinceLastUpdate >= this.REFRESH_INTERVAL_MS) {
      this.executeMetricsUpdate();
    }
  }

  private executeMetricsUpdate(): void {
    const { productionOrderCode, productionOrderCycleCode, branchCode } = this.countDetails;

    forkJoin({
      actual: this.productionOrderService.getActualProductiveTime(
        branchCode,
        productionOrderCycleCode,
        productionOrderCode,
      ),
      progress: this.productionOrderService.getQuantityProgress(
        branchCode,
        productionOrderCycleCode,
        productionOrderCode,
      ),
      rhythm: this.productionOrderService.getRhythmAnalysis(
        branchCode,
        productionOrderCycleCode,
        productionOrderCode,
      ),
    }).subscribe({
      next: res => {
        this.actualProductiveTime.set(res.actual.totalActualMinutes);
        this.quantityProgress.set(res.progress);

        this.setupRhythmGauge(res.rhythm);

        this.lastMetricsUpdate.set(Date.now());
      },
      error: err => console.error('Erro ao atualizar métricas dinâmicas:', err),
    });
  }

  private setupRhythmGauge(rhythmData: any): void {
    if (!rhythmData || rhythmData.idealTimePerPiece === 0) {
      this.rhythmDescription.set('Aguardando apontamentos...');
      this.rhythmPointerRotation.set(-90);
      return;
    }

    const ideal = rhythmData.idealTimePerPiece;
    const actual = rhythmData.actualTimePerPiece;
    const min = rhythmData.gaugeMin;
    const max = rhythmData.gaugeMax;

    let efficiencyPercentage = 0;
    if (actual > 0) {
      efficiencyPercentage = (ideal / actual) * 100;
    }
    this.rhythmCurrentPercentage.set(efficiencyPercentage);

    let pointerActual = actual;
    if (pointerActual > max) pointerActual = max;
    if (pointerActual < min) pointerActual = min;

    let visualPercentage = 0;

    if (pointerActual >= ideal) {
      if (max === ideal) {
        visualPercentage = 0.5;
      } else {
        const normalized = (pointerActual - ideal) / (max - ideal);
        visualPercentage = 0.5 - normalized * 0.5;
      }
    } else {
      if (ideal === min) {
        visualPercentage = 0.5;
      } else {
        const normalized = (ideal - pointerActual) / (ideal - min);
        visualPercentage = 0.5 + normalized * 0.5;
      }
    }

    if (visualPercentage < 0) visualPercentage = 0;
    if (visualPercentage > 1) visualPercentage = 1;

    const degrees = visualPercentage * 180 - 90;

    this.rhythmPointerRotation.set(degrees);
    this.rhythmDescription.set(
      `Minutos/peça ideal: ${ideal.toFixed(2)} | Atual: ${actual.toFixed(2)}`,
    );
  }
}
